import React from 'react';
import PropTypes from 'prop-types';

// Network Error Fallback
export const NetworkErrorFallback = ({ onRetry, onReset, errorId, retryCount, maxRetries }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '2rem',
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: '8px',
    margin: '1rem',
    textAlign: 'center'
  }}>
    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌐</div>
    <h2 style={{ color: '#856404', marginBottom: '1rem' }}>Connection Error</h2>
    <p style={{ color: '#856404', marginBottom: '1.5rem', lineHeight: '1.5' }}>
      We're having trouble connecting to our servers. Please check your internet connection and try again.
    </p>
    {errorId && <p style={{ fontSize: '0.875rem', color: '#856404', marginBottom: '1.5rem' }}>Error ID: {errorId}</p>}
    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
      {retryCount < maxRetries && (
        <button
          onClick={onRetry}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#ffc107',
            color: '#212529',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Retry Connection {retryCount > 0 && `(${retryCount}/${maxRetries})`}
        </button>
      )}
      <button
        onClick={onReset}
        style={{
          padding: '0.5rem 1rem',
          backgroundColor: '#6c757d',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Refresh Page
      </button>
    </div>
  </div>
);

// Authentication Error Fallback
export const AuthErrorFallback = ({ onReset, errorId }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '2rem',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '8px',
    margin: '1rem',
    textAlign: 'center'
  }}>
    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔐</div>
    <h2 style={{ color: '#721c24', marginBottom: '1rem' }}>Authentication Required</h2>
    <p style={{ color: '#721c24', marginBottom: '1.5rem', lineHeight: '1.5' }}>
      There was an issue with your authentication. Please log in again to continue.
    </p>
    {errorId && <p style={{ fontSize: '0.875rem', color: '#721c24', marginBottom: '1.5rem' }}>Error ID: {errorId}</p>}
    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
      <button
        onClick={() => window.location.href = '/login'}
        style={{
          padding: '0.5rem 1rem',
          backgroundColor: '#dc3545',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Go to Login
      </button>
      <button
        onClick={onReset}
        style={{
          padding: '0.5rem 1rem',
          backgroundColor: '#6c757d',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Refresh Page
      </button>
    </div>
  </div>
);

// Permission Error Fallback
export const PermissionErrorFallback = ({ onReset, errorId }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '2rem',
    backgroundColor: '#d1ecf1',
    border: '1px solid #bee5eb',
    borderRadius: '8px',
    margin: '1rem',
    textAlign: 'center'
  }}>
    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚫</div>
    <h2 style={{ color: '#0c5460', marginBottom: '1rem' }}>Access Denied</h2>
    <p style={{ color: '#0c5460', marginBottom: '1.5rem', lineHeight: '1.5' }}>
      You don't have permission to access this resource. Please contact your administrator if you believe this is an error.
    </p>
    {errorId && <p style={{ fontSize: '0.875rem', color: '#0c5460', marginBottom: '1.5rem' }}>Error ID: {errorId}</p>}
    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
      <button
        onClick={() => window.history.back()}
        style={{
          padding: '0.5rem 1rem',
          backgroundColor: '#17a2b8',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Go Back
      </button>
      <button
        onClick={() => window.location.href = '/dashboard'}
        style={{
          padding: '0.5rem 1rem',
          backgroundColor: '#28a745',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Dashboard
      </button>
    </div>
  </div>
);

// Chunk Loading Error Fallback
export const ChunkErrorFallback = ({ onReset, errorId }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '2rem',
    backgroundColor: '#e2e3e5',
    border: '1px solid #d6d8db',
    borderRadius: '8px',
    margin: '1rem',
    textAlign: 'center'
  }}>
    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📦</div>
    <h2 style={{ color: '#383d41', marginBottom: '1rem' }}>Application Update Required</h2>
    <p style={{ color: '#383d41', marginBottom: '1.5rem', lineHeight: '1.5' }}>
      The application failed to load properly. This usually happens when we've updated the app. Please refresh the page to get the latest version.
    </p>
    {errorId && <p style={{ fontSize: '0.875rem', color: '#383d41', marginBottom: '1.5rem' }}>Error ID: {errorId}</p>}
    <button
      onClick={() => window.location.reload()}
      style={{
        padding: '0.75rem 2rem',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '1rem'
      }}
    >
      Refresh Application
    </button>
  </div>
);

// Component Error Fallback
export const ComponentErrorFallback = ({ onRetry, onReset, errorId, retryCount, maxRetries, showErrorDetails, error, errorInfo }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '2rem',
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    margin: '1rem',
    textAlign: 'center'
  }}>
    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
    <h2 style={{ color: '#dc3545', marginBottom: '1rem' }}>Component Error</h2>
    <p style={{ color: '#6c757d', marginBottom: '1.5rem', lineHeight: '1.5' }}>
      A component failed to render properly. Our team has been notified and is working to fix this issue.
    </p>
    {errorId && <p style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '1.5rem' }}>Error ID: {errorId}</p>}
    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
      {retryCount < maxRetries && (
        <button
          onClick={onRetry}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#ffc107',
            color: '#212529',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Try Again {retryCount > 0 && `(${retryCount}/${maxRetries})`}
        </button>
      )}
      <button
        onClick={onReset}
        style={{
          padding: '0.5rem 1rem',
          backgroundColor: '#6c757d',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Reset Component
      </button>
    </div>
    
    {showErrorDetails && error && (
      <details style={{ 
        marginTop: '2rem', 
        textAlign: 'left',
        backgroundColor: '#f8f9fa',
        padding: '1rem',
        borderRadius: '4px',
        border: '1px solid #dee2e6',
        width: '100%',
        maxWidth: '600px'
      }}>
        <summary style={{ 
          cursor: 'pointer', 
          fontWeight: 'bold',
          marginBottom: '0.5rem'
        }}>
          Error Details (Development Only)
        </summary>
        <pre style={{ 
          fontSize: '0.875rem', 
          overflow: 'auto',
          maxHeight: '200px',
          whiteSpace: 'pre-wrap'
        }}>
          {error.toString()}
          {errorInfo && errorInfo.componentStack}
        </pre>
      </details>
    )}
  </div>
);

// Generic Error Fallback
export const GenericErrorFallback = ({ onRetry, onReset, errorId, retryCount, maxRetries, showErrorDetails, error, errorInfo }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '2rem',
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    margin: '1rem',
    textAlign: 'center'
  }}>
    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
    <h2 style={{ color: '#dc3545', marginBottom: '1rem' }}>Something Went Wrong</h2>
    <p style={{ color: '#6c757d', marginBottom: '1.5rem', lineHeight: '1.5' }}>
      An unexpected error occurred. Our team has been notified and is working to fix this issue.
    </p>
    {errorId && <p style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '1.5rem' }}>Error ID: {errorId}</p>}
    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
      {retryCount < maxRetries && (
        <button
          onClick={onRetry}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Try Again {retryCount > 0 && `(${retryCount}/${maxRetries})`}
        </button>
      )}
      <button
        onClick={onReset}
        style={{
          padding: '0.5rem 1rem',
          backgroundColor: '#6c757d',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Reset Page
      </button>
    </div>
    
    {showErrorDetails && error && (
      <details style={{ 
        marginTop: '2rem', 
        textAlign: 'left',
        backgroundColor: '#f8f9fa',
        padding: '1rem',
        borderRadius: '4px',
        border: '1px solid #dee2e6',
        width: '100%',
        maxWidth: '600px'
      }}>
        <summary style={{ 
          cursor: 'pointer', 
          fontWeight: 'bold',
          marginBottom: '0.5rem'
        }}>
          Error Details (Development Only)
        </summary>
        <pre style={{ 
          fontSize: '0.875rem', 
          overflow: 'auto',
          maxHeight: '200px',
          whiteSpace: 'pre-wrap'
        }}>
          {error.toString()}
          {errorInfo && errorInfo.componentStack}
        </pre>
      </details>
    )}
  </div>
);

// PropTypes for all fallback components
const commonPropTypes = {
  errorId: PropTypes.string,
  onRetry: PropTypes.func,
  onReset: PropTypes.func,
  retryCount: PropTypes.number,
  maxRetries: PropTypes.number,
  showErrorDetails: PropTypes.bool,
  error: PropTypes.object,
  errorInfo: PropTypes.object
};

NetworkErrorFallback.propTypes = commonPropTypes;
AuthErrorFallback.propTypes = commonPropTypes;
PermissionErrorFallback.propTypes = commonPropTypes;
ChunkErrorFallback.propTypes = commonPropTypes;
ComponentErrorFallback.propTypes = commonPropTypes;
GenericErrorFallback.propTypes = commonPropTypes;
